// Backend endpoint definitions, grouped one object per domain (auth, users,
// vehicles, cast management, ...).
//
// This module is the import surface the rest of the app uses
// (`../services/api`), so anything moved out of it is re-exported below
// rather than re-pointed at every call site.
//
// The shared request machinery — base URL, CSRF handling, the auth-bootstrap
// gate, ApiError — lives in `./apiClient`; only endpoint paths and payloads
// belong here. When this file approaches the 800 LOC limit again, lift a
// whole domain object into its own module and re-export it from here, the
// same way the transport layer was lifted out.

import type {
  LoginResponse,
  RegisterResponse,
  RouteCalculationResponse,
  DriverStatus,
  ActiveDriversResponse,
  BulkCreateTestRequestsResponse,
} from '../types/api';
import type { User, Route, Vehicle, Address, CommuteRequest, CommuteGroup, Shop, CorporateGroup, NgConstraint, Announcement, Inquiry } from '../types/models';
import api from './apiClient';
import type { ApiError, ApiResponse } from './apiClient';

// Kept exported from here because call sites import them from
// `../services/api`; the definitions moved to ./apiClient.
export { ApiError, setCsrfToken, setAuthBootstrap } from './apiClient';
export type { ApiResponse } from './apiClient';

export const authAPI = {
  // Legacy endpoints (custom JWT auth)
  login: (email: string, password: string): Promise<ApiResponse<LoginResponse>> => api.post<LoginResponse>('/auth/login', { email, password }),
  getCurrentUser: (): Promise<ApiResponse<User>> => api.get<User>('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string): Promise<ApiResponse> => api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),
  requestPasswordReset: (email: string): Promise<ApiResponse> => api.post('/auth/request-password-reset', { email }),
  resetPassword: (token: string, newPassword: string): Promise<ApiResponse> => api.post('/auth/reset-password', {
    token,
    new_password: newPassword
  }),

  // Logout (clears httpOnly cookies on server)
  logout: (): Promise<ApiResponse> => api.post('/auth/logout'),

  // Supabase Auth endpoints (v2)
  registerV2: (userData: Record<string, unknown>): Promise<ApiResponse<RegisterResponse>> => api.post<RegisterResponse>('/auth/register-v2', userData),
  getCurrentUserV2: (): Promise<ApiResponse<User>> => api.get<User>('/auth/me-v2'),
  requestPasswordResetV2: (email: string): Promise<ApiResponse> => api.post('/auth/request-password-reset-v2', { email }),
};

export const commuteRequestAPI = {
  createRequest: (data: Record<string, unknown>): Promise<ApiResponse<CommuteRequest>> => api.post<CommuteRequest>('/commute-requests', data),
  getMyRequests: (): Promise<ApiResponse<CommuteRequest[]>> => api.get<CommuteRequest[]>('/commute-requests/my'),
  getPendingRequests: (shopId: number): Promise<ApiResponse<CommuteRequest[]>> => api.get<CommuteRequest[]>(`/commute-requests/shop/${shopId}/pending`),
  getGroupPendingRequests: (groupId: number): Promise<ApiResponse<CommuteRequest[]>> => api.get<CommuteRequest[]>(`/commute-requests/group/${groupId}/pending`),
  getMyPendingRequest: (): Promise<ApiResponse<CommuteRequest | null>> => api.get<CommuteRequest | null>('/commute-requests/my-pending'),
  updateRequest: (requestId: number, data: Record<string, unknown>): Promise<ApiResponse<CommuteRequest>> => api.put<CommuteRequest>(`/commute-requests/${requestId}`, data),
  cancelMyRequest: (requestId: number): Promise<ApiResponse<CommuteRequest>> => api.put<CommuteRequest>(`/commute-requests/${requestId}/cancel`),
  deleteMyRequest: (requestId: number): Promise<ApiResponse> => api.delete(`/commute-requests/${requestId}`),
};

export const routeAPI = {
  // Corporate Groups対応: group_id を使用（後方互換性あり）
  calculateRoutes: async (groupIdOrShopId: number, options: { signal?: AbortSignal } = {}): Promise<ApiResponse<RouteCalculationResponse>> => {
    try {
      // Try group-level endpoint first
      // Support AbortSignal for cancellation
      return await api.post<RouteCalculationResponse>(`/routes/calculate/${groupIdOrShopId}`, null, {
        signal: options.signal
      });
    } catch (error) {
      // If 404, might be old backend - return error for now
      throw error;
    }
  },
  getLatestRoutes: async (groupIdOrShopId: number, limit: number = 10): Promise<ApiResponse<Route[]>> => {
    try {
      // Try group-level endpoint first
      return await api.get<Route[]>(`/routes/group/${groupIdOrShopId}/latest`, { params: { limit } });
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as ApiError;
        if (apiError.response?.status === 404) {
          // Fallback to shop-level endpoint (old backend)
          return await api.get<Route[]>(`/routes/shop/${groupIdOrShopId}/latest`, { params: { limit } });
        }
      }
      throw error;
    }
  },
  getMyRoute: (): Promise<ApiResponse> => api.get('/routes/my-route'),
  confirmRoute: (routeId: number): Promise<ApiResponse> => api.post(`/routes/confirm/${routeId}`),
  unconfirmRoute: (routeId: number): Promise<ApiResponse> => api.post(`/routes/unconfirm/${routeId}`),
  // Phase 4: Manual route member editing
  addMemberToRoute: (routeId: number, userId: number): Promise<ApiResponse> => api.post(`/routes/${routeId}/members/add`, { user_id: userId }),
  removeMemberFromRoute: (routeId: number, userId: number): Promise<ApiResponse> => api.delete(`/routes/${routeId}/members/${userId}`),
  getAvailableMembersForRoute: (routeId: number): Promise<ApiResponse> => api.get(`/routes/${routeId}/available-members-for-route`),
  // Phase 5: TSP optimization
  optimizeRouteOrder: (routeId: number): Promise<ApiResponse> => api.post(`/routes/${routeId}/optimize-order`),
  // Phase 6: Drag and drop reordering
  reorderStops: (routeId: number, stopOrders: Array<{ detail_id: number; stop_order: number }>): Promise<ApiResponse> => api.put(`/routes/${routeId}/reorder`, stopOrders),
  // Phase 7: Route management
  deleteRoute: (routeId: number): Promise<ApiResponse> => api.delete(`/routes/${routeId}`),
  createRoute: (vehicleId: number, shopId: number): Promise<ApiResponse> => api.post('/routes/create', { vehicle_id: vehicleId, shop_id: shopId }),
};

export const vehicleAPI = {
  getShopVehicles: (shopId: number): Promise<ApiResponse<Vehicle[]>> => api.get<Vehicle[]>(`/vehicles/shop/${shopId}`),
  getGroupVehicles: (groupId: number): Promise<ApiResponse<Vehicle[]>> => api.get<Vehicle[]>(`/vehicles/group/${groupId}`),
  createVehicle: (data: Record<string, unknown>): Promise<ApiResponse<Vehicle>> => api.post<Vehicle>('/vehicles', data),
  updateVehicle: (vehicleId: number, data: Record<string, unknown>): Promise<ApiResponse<Vehicle>> => api.put<Vehicle>(`/vehicles/${vehicleId}`, data),
  assignDriver: (vehicleId: number, driverId: number | null): Promise<ApiResponse<Vehicle>> => api.patch<Vehicle>(`/vehicles/${vehicleId}/assign-driver`, { driver_id: driverId || null }),
  deleteVehicle: (vehicleId: number): Promise<ApiResponse> => api.delete(`/vehicles/${vehicleId}`),
  setFinalRoute: (vehicleId: number): Promise<ApiResponse<Vehicle>> => api.post<Vehicle>(`/vehicles/${vehicleId}/set-final-route`),
  cancelFinalRoute: (vehicleId: number): Promise<ApiResponse<Vehicle>> => api.post<Vehicle>(`/vehicles/${vehicleId}/cancel-final-route`),
};

export const driverAttendanceAPI = {
  // Route and passenger management
  getMyRoute: (): Promise<ApiResponse<Route | null>> => api.get<Route | null>('/drivers/my-route'),
  updatePassengerStatus: (detailId: number, status: string): Promise<ApiResponse> => api.post(`/drivers/passenger-status/${detailId}`, { status }),
  getUsageHistory: (startDate: string, endDate: string): Promise<ApiResponse> => api.get('/drivers/usage-history', {
    params: { start_date: startDate, end_date: endDate }
  }),
  // Attendance
  checkIn: (): Promise<ApiResponse> => api.post('/drivers/check-in'),
  checkOut: (distanceKm: number): Promise<ApiResponse> => api.post('/drivers/check-out', { distance_km: distanceKm }),
  getMyStatus: (): Promise<ApiResponse<DriverStatus>> => api.get<DriverStatus>('/drivers/my-status'),
  getAttendance: (driverId: number | null, startDate: string, endDate: string): Promise<ApiResponse> => api.get('/drivers/attendance', {
    params: { driver_id: driverId, start_date: startDate, end_date: endDate }
  }),
  exportCsv: (year: number, month: number): Promise<ApiResponse> => api.get('/drivers/attendance/export', {
    params: { year, month },
    responseType: 'blob'
  }),
  getActiveDrivers: (): Promise<ApiResponse<ActiveDriversResponse>> => api.get<ActiveDriversResponse>('/drivers/active-drivers'),
  getMyAlerts: (): Promise<ApiResponse> => api.get('/drivers/my-alerts'),
  dismissAlert: (alertId: number): Promise<ApiResponse> => api.post(`/drivers/dismiss-alert/${alertId}`),
  dismissAllAlerts: (): Promise<ApiResponse> => api.post('/drivers/dismiss-all-alerts'),
  getMyVehicleStatus: (): Promise<ApiResponse<Vehicle | null>> => api.get<Vehicle | null>('/drivers/my-vehicle-status'),
  updateVehicleStatus: (status: string): Promise<ApiResponse<Vehicle>> => api.post<Vehicle>('/drivers/update-vehicle-status', { status }),
  acknowledgeFinalRoute: (): Promise<ApiResponse> => api.post('/drivers/acknowledge-final-route'),
  // Admin endpoints
  adminCheckInDriver: (driverId: number): Promise<ApiResponse> => api.post('/drivers/admin/check-in-driver', { driver_id: driverId }),
  adminCheckOutDriver: (driverId: number, distanceKm: number): Promise<ApiResponse> => api.post('/drivers/admin/check-out-driver', {
    driver_id: driverId,
    distance_km: distanceKm
  }),
};

export const shopAPI = {
  getShop: (shopId: number): Promise<ApiResponse<Shop>> => api.get<Shop>(`/shops/${shopId}`),
  updateTimeRestrictions: (shopId: number, enabled: boolean, startTime?: string, endTime?: string): Promise<ApiResponse<Shop>> => {
    const params = new URLSearchParams({ enabled: enabled.toString() });
    if (enabled && startTime) params.append('start_time', startTime);
    if (enabled && endTime) params.append('end_time', endTime);
    return api.patch<Shop>(`/shops/${shopId}/time-restrictions?${params.toString()}`);
  },
};

export const adminAPI = {
  clearRequests: (shopId: number): Promise<ApiResponse> => api.post(`/admin/clear-requests/${shopId}`),
  changeCastStatus: (userId: number, newStatus: string): Promise<ApiResponse<CommuteRequest>> => api.patch<CommuteRequest>(`/admin/cast-status/${userId}`, { new_status: newStatus }),
  deleteCommuteRequest: (requestId: number): Promise<ApiResponse> => api.delete(`/admin/commute-request/${requestId}`),
  updateDepartureTime: (requestId: number, departureTime: string): Promise<ApiResponse<CommuteRequest>> => api.patch<CommuteRequest>(`/admin/commute-request/${requestId}/departure-time`, { departure_time: departureTime }),
  emergencyRecall: (userId: number, deleteRoute: boolean): Promise<ApiResponse> => api.post(`/admin/emergency-recall/${userId}`, { delete_route: deleteRoute }),
  getNgConstraints: (shopId: number): Promise<ApiResponse<NgConstraint[]>> => api.get<NgConstraint[]>(`/admin/ng-constraints/${shopId}`),
  createNgConstraint: (shopId: number, userId1: number, userId2: number, reason: string): Promise<ApiResponse<NgConstraint>> => api.post<NgConstraint>('/admin/ng-constraints', {
    shop_id: shopId, user_id_1: userId1, user_id_2: userId2, reason
  }),
  deleteNgConstraint: (ngId: number): Promise<ApiResponse> => api.delete(`/admin/ng-constraints/${ngId}`),
  getShopUsers: (shopId: number): Promise<ApiResponse<User[]>> => api.get<User[]>(`/users/shop/${shopId}`),
  bulkCreateTestRequests: (shopId: number): Promise<ApiResponse<BulkCreateTestRequestsResponse>> => api.post<BulkCreateTestRequestsResponse>(`/commute-requests/bulk-create-test/${shopId}`),
};

// Corporate Groups API（後方互換性あり）
export const corporateGroupsAPI = {
  getGroup: async (groupId: number): Promise<ApiResponse<CorporateGroup>> => {
    try {
      return await api.get<CorporateGroup>(`/corporate-groups/${groupId}`);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as ApiError;
        if (apiError.response?.status === 404) {
          // Return minimal group data (old backend detected)
          return { data: { group_id: groupId, name: 'Default Group' } as CorporateGroup } as ApiResponse<CorporateGroup>;
        }
      }
      throw error;
    }
  },
  getGroupShops: async (groupId: number): Promise<ApiResponse<Shop[]>> => {
    try {
      return await api.get<Shop[]>(`/corporate-groups/${groupId}/shops`);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as ApiError;
        if (apiError.response?.status === 404) {
          // Old backend detected, return empty list
          return { data: [] as Shop[] } as ApiResponse<Shop[]>;
        }
      }
      throw error;
    }
  },
  getWaitingLocation: async (groupIdOrShopId: number): Promise<ApiResponse> => {
    try {
      // Try group-level endpoint first
      return await api.get(`/corporate-groups/${groupIdOrShopId}/waiting-location`);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as ApiError;
        if (apiError.response?.status === 404) {
          // Fallback to shop-level endpoint (old backend)
          return await api.get(`/admin/shop/${groupIdOrShopId}/waiting-location`);
        }
      }
      throw error;
    }
  },
  updateWaitingLocation: async (groupIdOrShopId: number, address: string, lat?: number | null, lng?: number | null): Promise<ApiResponse> => {
    // Build body object - omit lat/lng if not provided (backend will geocode)
    const body: Record<string, unknown> = { waiting_location_address: address };
    if (lat !== null && lat !== undefined) body.waiting_location_lat = lat;
    if (lng !== null && lng !== undefined) body.waiting_location_lng = lng;

    try {
      // Try group-level endpoint first
      return await api.put(`/corporate-groups/${groupIdOrShopId}/waiting-location`, body);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as ApiError;
        if (apiError.response?.status === 404) {
          // Fallback to shop-level endpoint (old backend)
          return await api.put(`/admin/shop/${groupIdOrShopId}/waiting-location`, body);
        }
      }
      throw error;
    }
  },
  deleteWaitingLocation: async (groupIdOrShopId: number): Promise<ApiResponse> => {
    try {
      // Try group-level endpoint first
      return await api.delete(`/corporate-groups/${groupIdOrShopId}/waiting-location`);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as ApiError;
        if (apiError.response?.status === 404) {
          // Fallback to shop-level endpoint (old backend)
          return await api.delete(`/admin/shop/${groupIdOrShopId}/waiting-location`);
        }
      }
      throw error;
    }
  },
  getTimeRestrictions: async (groupId: number): Promise<ApiResponse> => {
    return await api.get(`/corporate-groups/${groupId}/time-restrictions`);
  },
  updateTimeRestrictions: async (groupId: number, enabled: boolean, startTime?: string, endTime?: string): Promise<ApiResponse> => {
    const body: Record<string, unknown> = { enabled };
    if (enabled && startTime) body.start_time = startTime;
    if (enabled && endTime) body.end_time = endTime;
    return await api.patch(`/corporate-groups/${groupId}/time-restrictions`, body);
  },
  // Usage Fee Settings
  getUsageFeeSettings: async (groupId: number): Promise<ApiResponse> => {
    return await api.get(`/corporate-groups/${groupId}/usage-fee-settings`);
  },
  updateUsageFeeSettings: async (groupId: number, feeType: string, flatRateAmount?: number): Promise<ApiResponse> => {
    return await api.put(`/corporate-groups/${groupId}/usage-fee-settings`, {
      fee_type: feeType,
      flat_rate_amount: flatRateAmount || 0,
    });
  },
  updateUsageFeeDistanceTiers: async (groupId: number, tiers: Array<{ min_km: number; max_km: number; amount: number }>): Promise<ApiResponse> => {
    return await api.put(`/corporate-groups/${groupId}/usage-fee-distance-tiers`, { tiers });
  },
};

export const addressAPI = {
  getMyAddresses: (): Promise<ApiResponse<Address[]>> => api.get<Address[]>('/addresses/my'),
  getUserAddresses: (userId: number): Promise<ApiResponse<Address[]>> => api.get<Address[]>(`/addresses/user/${userId}`),
  createAddress: (data: Record<string, unknown>): Promise<ApiResponse<Address>> => api.post<Address>('/addresses', data),
  updateAddress: (addressId: number, data: Record<string, unknown>): Promise<ApiResponse<Address>> => api.put<Address>(`/addresses/${addressId}`, data),
  deleteAddress: (addressId: number): Promise<ApiResponse> => api.delete(`/addresses/${addressId}`),
};

export const userAPI = {
  getShopUsers: (shopId: number, options: { forNgManagement?: boolean } = {}): Promise<ApiResponse<User[]>> => {
    const params: Record<string, unknown> = {};
    if (options.forNgManagement) {
      params.for_ng_management = true;
    }
    return api.get<User[]>(`/users/shop/${shopId}`, { params });
  },
  getGroupCasts: (): Promise<ApiResponse<User[]>> => api.get<User[]>('/users/group/casts'),
  getGroupDrivers: (): Promise<ApiResponse<User[]>> => api.get<User[]>('/users/group/drivers'),
  createUser: (data: Record<string, unknown>): Promise<ApiResponse<User>> => api.post<User>('/users', data),
  updateUser: (userId: number, data: Record<string, unknown>): Promise<ApiResponse<User>> => api.put<User>(`/users/${userId}`, data),
  deleteUser: (userId: number): Promise<ApiResponse> => api.delete(`/users/${userId}`),
};

export const pushAPI = {
  getVapidPublicKey: (): Promise<ApiResponse> => api.get('/push/vapid-public-key'),
  subscribe: (subscription: Record<string, unknown>): Promise<ApiResponse> => api.post('/push/subscribe', subscription),
  unsubscribe: (endpoint: string): Promise<ApiResponse> => api.post('/push/unsubscribe', { endpoint }),
  testNotification: (): Promise<ApiResponse> => api.post('/push/test'),
  getSubscriptionStatus: (): Promise<ApiResponse> => api.get('/push/subscription-status'),
};

export const commuteGroupsAPI = {
  getAll: (): Promise<ApiResponse<CommuteGroup[]>> => api.get<CommuteGroup[]>('/commute-groups/'),
  get: (groupId: number): Promise<ApiResponse<CommuteGroup>> => api.get<CommuteGroup>(`/commute-groups/${groupId}`),
  create: (data: Record<string, unknown>): Promise<ApiResponse<CommuteGroup>> => api.post<CommuteGroup>('/commute-groups/', data),
  update: (groupId: number, data: Record<string, unknown>): Promise<ApiResponse<CommuteGroup>> => api.put<CommuteGroup>(`/commute-groups/${groupId}`, data),
  delete: (groupId: number): Promise<ApiResponse> => api.delete(`/commute-groups/${groupId}`),
  getMembers: (groupId: number): Promise<ApiResponse<User[]>> => api.get<User[]>(`/commute-groups/${groupId}/members`),
  addMember: (groupId: number, userId: number): Promise<ApiResponse<User>> => api.post<User>(`/commute-groups/${groupId}/members`, { user_id: userId }),
  removeMember: (groupId: number, userId: number): Promise<ApiResponse> => api.delete(`/commute-groups/${groupId}/members/${userId}`),
  toggleCalculation: (groupId: number): Promise<ApiResponse<CommuteGroup>> => api.patch<CommuteGroup>(`/commute-groups/${groupId}/toggle`),
  getActiveGroups: (): Promise<ApiResponse<CommuteGroup[]>> => api.get<CommuteGroup[]>('/commute-groups/active/list'),
};

// Cast Management API (Admin only)
export const castManagementAPI = {
  // Cast Attendance
  markAttendance: (data: Record<string, unknown>): Promise<ApiResponse> => api.post('/cast-management/attendance', data),
  getShopAttendance: (shopId: number, businessDate: string | null = null): Promise<ApiResponse> =>
    api.get(`/cast-management/attendance/shop/${shopId}`, {
      params: businessDate ? { business_date: businessDate } : {}
    }),
  getGroupAttendance: (businessDate: string | null = null): Promise<ApiResponse> =>
    api.get('/cast-management/attendance/group', {
      params: businessDate ? { business_date: businessDate } : {}
    }),
  getMyAttendanceStatus: (): Promise<ApiResponse> =>
    api.get('/cast-management/attendance/my-status'),

  // Cast Daily Transport Usage - self service.
  // "daily" is historical: the backend stores this in cast_transport_usage,
  // one row per user with no business_date, so nothing resets at 10:00 AM JST.
  getMyDailyTransportUsage: (): Promise<ApiResponse> =>
    api.get('/cast-management/daily-transport-usage/my'),
  setMyDailyTransportUsage: (usesTransport: boolean): Promise<ApiResponse> =>
    api.post('/cast-management/daily-transport-usage/my', { uses_transport: usesTransport }),

  // Cast Daily Transport Usage
  setDailyTransportUsage: (data: Record<string, unknown>): Promise<ApiResponse> => api.post('/cast-management/daily-transport-usage', data),
  getShopDailyTransportUsage: (shopId: number, businessDate: string | null = null): Promise<ApiResponse> =>
    api.get(`/cast-management/daily-transport-usage/shop/${shopId}`, {
      params: businessDate ? { business_date: businessDate } : {}
    }),
  getGroupDailyTransportUsage: (businessDate: string | null = null): Promise<ApiResponse> =>
    api.get('/cast-management/daily-transport-usage/group', {
      params: businessDate ? { business_date: businessDate } : {}
    }),

  // Cast Daily Scheduled Departure Time (resets at 10:00 AM JST)
  setDailyScheduledDepartureTime: (data: Record<string, unknown>): Promise<ApiResponse> => api.post('/cast-management/daily-scheduled-departure-time', data),
  getUserDailyScheduledDepartureTime: (userId: number, businessDate: string | null = null): Promise<ApiResponse> =>
    api.get(`/cast-management/daily-scheduled-departure-time/user/${userId}`, {
      params: businessDate ? { business_date: businessDate } : {}
    }),
  getShopDailyScheduledDepartureTimes: (shopId: number, businessDate: string | null = null): Promise<ApiResponse> =>
    api.get(`/cast-management/daily-scheduled-departure-time/shop/${shopId}`, {
      params: businessDate ? { business_date: businessDate } : {}
    }),
  getGroupDailyScheduledDepartureTimes: (businessDate: string | null = null): Promise<ApiResponse> =>
    api.get('/cast-management/daily-scheduled-departure-time/group', {
      params: businessDate ? { business_date: businessDate } : {}
    }),
};

// System Administration API (Super Admin only)
export const systemAPI = {
  // Statistics
  getOverview: (groupId: number | null = null): Promise<ApiResponse> => api.get('/system/stats/overview', { params: groupId ? { group_id: groupId } : {} }),
  getShopStats: (days: number = 7): Promise<ApiResponse> => api.get('/system/stats/shops', { params: { days } }),
  getActivity: (days: number = 7): Promise<ApiResponse> => api.get('/system/stats/activity', { params: { days } }),

  // Geocoding
  geocodeAddress: (address: string): Promise<ApiResponse> =>
    api.get('/system/shops/geocode', { params: { address } }),

  // Shop Management
  listShops: (): Promise<ApiResponse> => api.get('/system/shops'),
  createShop: (name: string, address: string, lat: number | null, lng: number | null, groupId: number | null): Promise<ApiResponse> =>
    api.post('/system/shops', {
      name,
      address,
      lat: lat || undefined,
      lng: lng || undefined,
      group_id: groupId || null,
    }),
  updateShop: (shopId: number, name: string, groupId: number | null): Promise<ApiResponse> =>
    api.patch(`/system/shops/${shopId}`, {
      name,
      group_id: groupId || null
    }),
  deleteShop: (shopId: number): Promise<ApiResponse> => api.delete(`/system/shops/${shopId}`),
  updateShopBilling: (shopId: number, billingPlan: string, isActive: boolean, trialEndDate: string | null): Promise<ApiResponse> =>
    api.patch(`/system/shops/${shopId}/billing`, {
      billing_plan: billingPlan,
      is_active: isActive,
      trial_end_date: trialEndDate
    }),

  // Shop Details
  getShopDetails: (shopId: number): Promise<ApiResponse> => api.get(`/system/shops/${shopId}/details`),
  getShopUsers: (shopId: number): Promise<ApiResponse> => api.get(`/system/shops/${shopId}/users`),
  getShopVehicles: (shopId: number): Promise<ApiResponse> => api.get(`/system/shops/${shopId}/vehicles`),
  getShopRecentRequests: (shopId: number, limit: number = 20): Promise<ApiResponse> =>
    api.get(`/system/shops/${shopId}/recent-requests`, { params: { limit } }),
  getShopRecentRoutes: (shopId: number, limit: number = 20): Promise<ApiResponse> =>
    api.get(`/system/shops/${shopId}/recent-routes`, { params: { limit } }),

  // Admin User Management
  listAdmins: (): Promise<ApiResponse> => api.get('/system/admins'),
  createAdmin: (shopId: number, name: string, email: string, password: string, role: string = 'shop_admin'): Promise<ApiResponse> =>
    api.post('/system/admins', {
      shop_id: shopId, name, email, password, role
    }),
  deleteAdmin: (userId: number): Promise<ApiResponse> => api.delete(`/system/admins/${userId}`),

  // Corporate Groups Management
  listGroups: (): Promise<ApiResponse> => api.get('/system/groups'),
  createGroup: (name: string, description: string, routingStrategy: string): Promise<ApiResponse> =>
    api.post('/system/groups', {
      name,
      description,
      routing_strategy: routingStrategy
    }),
  updateGroup: (groupId: number, name: string, description: string, routingStrategy: string): Promise<ApiResponse> =>
    api.patch(`/system/groups/${groupId}`, {
      name,
      description,
      routing_strategy: routingStrategy
    }),
  deleteGroup: (groupId: number): Promise<ApiResponse> => api.delete(`/system/groups/${groupId}`),

  // Monitoring & Health
  getErrorLogs: (hours: number = 24, severity: string | null = null, errorType: string | null = null, limit: number = 100): Promise<ApiResponse> =>
    api.get('/system/monitoring/errors', {
      params: { hours, severity, error_type: errorType, limit }
    }),
  getSystemHealth: (): Promise<ApiResponse> => api.get('/system/monitoring/health'),
  getPerformanceMetrics: (hours: number = 24): Promise<ApiResponse> =>
    api.get('/system/monitoring/performance', { params: { hours } }),
  getActivityTimeline: (hours: number = 24): Promise<ApiResponse> =>
    api.get('/system/monitoring/activity-timeline', { params: { hours } }),

  // Announcements Management
  listAnnouncements: (): Promise<ApiResponse> => api.get('/system/announcements'),
  createAnnouncement: (title: string, content: string, tag: string, isPublished: boolean = true): Promise<ApiResponse> =>
    api.post('/system/announcements', { title, content, tag, is_published: isPublished }),
  updateAnnouncement: (id: number, data: Record<string, unknown>): Promise<ApiResponse> => api.patch(`/system/announcements/${id}`, data),
  deleteAnnouncement: (id: number): Promise<ApiResponse> => api.delete(`/system/announcements/${id}`),

  // Inquiries Management
  listInquiries: (status?: string): Promise<ApiResponse> =>
    api.get('/system/inquiries', { params: status ? { status } : {} }),
  updateInquiry: (id: number, data: Record<string, unknown>): Promise<ApiResponse> => api.patch(`/system/inquiries/${id}`, data),
  deleteInquiry: (id: number): Promise<ApiResponse> => api.delete(`/system/inquiries/${id}`),

  // Audit Logs
  getAuditLogs: (hours: number = 168, action: string | null = null, targetType: string | null = null, limit: number = 100, shopId: number | null = null): Promise<ApiResponse> =>
    api.get('/system/audit-logs', { params: { hours, action, target_type: targetType, limit, shop_id: shopId } }),

  // Billing Report
  getBillingReport: (days: number = 30): Promise<ApiResponse> =>
    api.get('/system/billing/report', { params: { days } }),

  // CSV Import
  importShopsCSV: (file: File): Promise<ApiResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/system/import/shops', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importUsersCSV: (file: File): Promise<ApiResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/system/import/users', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Server Metrics
  getServerMetrics: (): Promise<ApiResponse> => api.get('/system/monitoring/server-metrics'),
};

// Public API (no authentication required)
export const publicAPI = {
  getAnnouncements: (): Promise<ApiResponse<Announcement[]>> => api.get<Announcement[]>('/public/announcements'),
  submitInquiry: (name: string, email: string, company: string, subject: string, message: string): Promise<ApiResponse<Inquiry>> =>
    api.post<Inquiry>('/public/inquiries', { name, email, company, subject, message }),
};

export default api;
